import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CoreRouterActions } from '@fantaz/core';
import {
  ContactPointFormActionTypes,
  updateMFAPreferenceMfa,
} from '@fantaz/profile/data-access';
import {
  AuthService,
  AuthServiceInjectionToken,
} from '@fantaz/shared/data-access/api';
import { differenceInSeconds } from '@fantaz/shared/utils/date';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  map,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import * as VerifyUserIdentityActions from './verify-user-identity.actions';
import {
  selectLastVerifyUserIdentity,
  selectVerifyUserIdentityAction,
} from './verify-user-identity.selectors';
import { CoreSnackbarActions } from '@fantaz/core/state';

@Injectable()
export class VerifyUserIdentityEffects {
  private actions$ = inject(Actions);
  private store: Store = inject(Store);
  private authService: AuthService = inject(AuthServiceInjectionToken);
  #activatedRoute = inject(ActivatedRoute);

  /**
   * Effect that listens for the 'openVerifyUserIdentity' action and navigates to the 'verify-user-identity' route.
   *
   * This effect is triggered when the 'openVerifyUserIdentity' action is dispatched to the store.
   * It navigates to the 'verify-user-identity' route in a dialog outlet, relative to the parent of the current activated route.
   * The 'replaceUrl' option is set to true, which means that the URL will be replaced rather than pushing a new state into the history.
   */
  openIdentityVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserIdentityActions.openVerifyUserIdentity),
      withLatestFrom(this.store.select(selectLastVerifyUserIdentity)),
      switchMap(([_, lastVerifyUserIdentity]) => {
        if (
          lastVerifyUserIdentity?.identityVerifiedAt &&
          differenceInSeconds(
            new Date(),
            lastVerifyUserIdentity.identityVerifiedAt,
          ) < 60
        ) {
          return of(VerifyUserIdentityActions.loadVerifyUserIdentitySuccess());
        }
        return [
          CoreRouterActions.navigate(
            ['', { outlets: { dialog: ['verify-user-identity'] } }],
            { relativeTo: this.#activatedRoute.parent, replaceUrl: true },
          ),
        ];
      }),
    ),
  );
  initUserIdentity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserIdentityActions.initVerifyUserIdentity),
      switchMap(({ password }) => {
        return this.authService
          .updatePassword({ newPassword: password, oldPassword: password })
          .pipe(
            map(() =>
              VerifyUserIdentityActions.loadVerifyUserIdentitySuccess(),
            ),
            catchError((error: any) => {
              let message = `We can't validate your password. Please try after some time.`;
              if (error.name === 'LimitExceededException') {
                message = `Sorry, you have reached the maximum number of password attempts. Please try again after a few seconds.`;
              }

              return [
                CoreSnackbarActions.showSnackbarError({
                  message,
                  config: {
                    duration: 5000,
                  },
                }),
                VerifyUserIdentityActions.loadVerifyUserIdentityFailure({
                  error,
                }),
              ];
            }),
          );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(
          VerifyUserIdentityActions.loadVerifyUserIdentityFailure({ error }),
        );
      }),
    ),
  );

  /**
   * Effect that listens for the 'openVerifyUserIdentity' action and navigates to the 'verify-user-identity' route.
   *
   * This effect is triggered when the 'openVerifyUserIdentity' action is dispatched to the store.
   * It navigates to the 'verify-user-identity' route in a dialog outlet, relative to the parent of the current activated route.
   * The 'replaceUrl' option is set to true, which means that the URL will be replaced rather than pushing a new state into the history.
   */
  loadVerifyUserIdentitySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserIdentityActions.loadVerifyUserIdentitySuccess),
      withLatestFrom(this.store.select(selectVerifyUserIdentityAction)),
      concatMap(([_, action]) => {
        let result = [];
        if (action) {
          result = this.getAction(action);
        }

        return result;
      }),
    ),
  );

  /**
   * Returns an array of actions based on the provided action key.
   *
   * This function is used to determine which actions should be performed based on the provided action key.
   * It returns an array of actions that should be dispatched to the store.
   *
   * @param {Object} action - The action to be dispatched. It should contain a key and optionally some data.
   * @param {any} action.data - The data to be included in the action. This could be any data related to the verification process.
   * @param {string} action.key - The key that identifies the type of action to be performed.
   * @returns {any[]} An array of actions to be dispatched to the store.
   */
  private getAction(action: { data?: any; key: string }): any[] {
    const result = [];
    switch (action.key) {
      case 'update-mfa-preference':
        // If the key is 'update-mfa-preference', add an updateMFAPreferenceMfa action to the result array
        result.push(
          CoreRouterActions.navigate(['', { outlets: { dialog: null } }], {
            replaceUrl: true,
          }),
          updateMFAPreferenceMfa({ updateMFAPreference: action.data }),
        );
        break;
      case 'authenticator-mfa':
        // If the key is 'authenticator-mfa', add a navigation action to the 'authenticator-mfa' route to the result array
        result.push(
          CoreRouterActions.navigate(
            ['', { outlets: { dialog: 'authenticator-mfa' } }],
            { relativeTo: this.#activatedRoute.parent, replaceUrl: true },
          ),
        );
        break;
      case 'disable-authenticator-mfa':
        // If the key is 'disable-authenticator-mfa', add a navigation action to the 'disable-authenticator-mfa' route to the result array
        result.push(
          CoreRouterActions.navigate(
            ['', { outlets: { dialog: 'disable-authenticator-mfa' } }],
            { relativeTo: this.#activatedRoute.parent, replaceUrl: true },
          ),
        );
        break;

      case 'change-contact-point':
        result.push(
          CoreRouterActions.navigate(
            [
              '',
              {
                outlets: {
                  dialog: ['change-contact-point', `${action.data.type}`],
                },
              },
            ],
            { relativeTo: this.#activatedRoute.parent, replaceUrl: true },
          ),
        );
        break;
      case 'add-contact-point':
        result.push(
          CoreRouterActions.navigate(
            [
              '',
              {
                outlets: {
                  dialog: ['add-contact-point', `${action.data.type}`],
                },
              },
            ],
            { relativeTo: this.#activatedRoute.parent, replaceUrl: true },
          ),
        );
        break;
      case 'init-contact-verification':
        result.push(
          ContactPointFormActionTypes.initVerification({
            attr: action.data.attr,
            value: action.data.value,
          }),
        );
        break;

      default:
        // If the key doesn't match any of the above cases, do nothing
        break;
    }
    return result;
  }
}
